import { Button } from "@/components/ui/button"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"

import { type ApiError, UsersService } from "@/client"
import useAuth from "@/hooks/useAuth"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"
import { useTranslation } from "react-i18next"

interface DeleteProps {
  open: boolean
  onClose: () => void
}

const DeleteConfirmation = ({ open, onClose }: DeleteProps) => {
  const { t } = useTranslation("common")
  const queryClient = useQueryClient()
  const { showSuccessToast } = useCustomToast()
  const {
    handleSubmit,
    formState: { isSubmitting },
  } = useForm()
  const { logout } = useAuth()

  const mutation = useMutation({
    mutationFn: () => UsersService.deleteUserMe(),
    onSuccess: () => {
      showSuccessToast(t("confirm.accountDeleted"))
      logout()
      onClose()
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] })
    },
  })

  const onSubmit = async () => {
    mutation.mutate()
  }

  return (
    <>
      <DialogRoot
        open={open}
        onExitComplete={onClose}
        size={{ base: "sm", md: "md" }}
        role="alertdialog"
      >
        <DialogBackdrop />
        <DialogContent as="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmRequired")}</DialogTitle>
            <DialogCloseTrigger />
          </DialogHeader>
          <DialogBody>{t("confirm.accountDeleteWarning")}</DialogBody>

          <DialogFooter gap={3}>
            <Button colorScheme="red" type="submit" loading={isSubmitting}>
              {t("actions.confirm")}
            </Button>
            <Button onClick={onClose} disabled={isSubmitting}>
              {t("actions.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogRoot>
    </>
  )
}

export default DeleteConfirmation
